/**
 * Navigation Menu Controller
 * 导航菜单交互控制器
 *
 * 功能:
 * - 桌面端：hover + click 触发下拉菜单
 * - 移动端：汉堡菜单 + 侧边抽屉动画
 * - 支持键盘导航 (Arrow keys, Enter, Escape)
 * - 点击外部区域关闭菜单
 */

class NavigationMenu {
  constructor() {
    // 菜单元素
    this.desktopMenuItems = document.querySelectorAll('.nav-item.has-submenu');
    this.mobileMenuBtn = document.querySelector('.hamburger-menu');
    this.mobileMenu = document.querySelector('.mobile-menu');
    this.mobileMenuOverlay = document.querySelector('.mobile-menu-overlay');

    // 搜索元素
    this.searchToggle = document.querySelector('.search-toggle');
    this.searchPanel = document.querySelector('.search-panel');
    this.searchPanelMask = document.querySelector('.search-panel-mask');
    this.searchBack = document.querySelector('.search-back');
    this.mobileSearchBack = document.querySelector('.mobile-search-back');
    this.searchResults = document.querySelector('.search-results');

    // 中文导航元素 (旧的)
      this.zhNavItem = document.querySelector('.nav-menu > li:nth-last-of-type(2)');
      this.zhNavDropdown = document.querySelector('.nav-menu > li:nth-last-of-type(2) .dropdown-menu');
    this.zhTeamToggle = document.querySelector('.zh-team-toggle');

    // 更多菜单元素
    this.moreMenuItem = document.querySelector('.more-menu-item');
    this.moreDropdown = document.querySelector('.more-dropdown');
    this.moreTeamToggle = document.querySelector('.more-team-toggle');
    this.moreZhToggle = document.querySelector('.more-zh-toggle');
    this.moreZhTeamToggle = document.querySelector('.more-zh-team-toggle');
    // 通过 nth-child 索引获取（第一个有子菜单的是 team，第二个是 zh）
    this.moreTeamLink = null;
    this.moreZhLink = null;

    // 状态
    this.isMobileMenuOpen = false;
    this.isSearchOpen = false;
    this.mobileMenuEventBound = false;
    this.isZhTeamExpanded = false;
    this.isZhNavHovered = false;
    this.isMoreTeamExpanded = false;
    this.isMoreZhExpanded = false;
    this.isMoreZhTeamExpanded = false;
    this.moreMenuView = null; // 'team', 'zh', or null

    // 初始化
    this.init();
  }

  init() {
    // 桌面端菜单
    this.initDesktopMenu();

    // 中文导航特殊处理 (旧的)
    this.initZhNav();

    // 更多菜单处理
    this.initMoreMenu();

    // 移动端菜单
    this.initMobileMenu();

    // 搜索面板
    this.initSearchPanel();

    // 键盘导航
    this.initKeyboardNavigation();

    // 滚动监听
    this.initScrollListener();

    // Search Panel 滚动监听 (延迟执行，等待 Vue 组件渲染)
    setTimeout(() => {
      this.initSearchPanelScrollListener();
    }, 100);
  }

  /**
   * 更多菜单处理
   */
  initMoreMenu() {
    if (!this.moreMenuItem || !this.moreDropdown) return;

    const link = this.moreMenuItem.querySelector('.nav-link');
    if (!link) return;

    // 为菜单项动态添加 data-more-view 和 data-more-link 属性（通过 nth-child 索引）
    const moreItemsWithSubmenu = Array.from(this.moreDropdown.querySelectorAll('.submenu-item.has-submenu'));
    moreItemsWithSubmenu.forEach((item, index) => {
      const viewName = index === 0 ? 'team' : 'zh';
      item.setAttribute('data-more-view', viewName);
      const itemLink = item.querySelector('.more-item-link');
      if (itemLink) {
        itemLink.setAttribute('data-more-link', viewName);
      }
    });

    // 重新获取链接元素（确保在 DOM 中）
    this.moreTeamLink = this.moreDropdown.querySelector('[data-more-link="team"]');
    this.moreZhLink = this.moreDropdown.querySelector('[data-more-link="zh"]');

    // Hover 打开
    this.moreMenuItem.addEventListener('mouseenter', () => {
      this.openDesktopSubmenu(this.moreMenuItem);
    });

    // Hover 关闭 - 在 768px-1279px 有子视图时禁用
    this.moreMenuItem.addEventListener('mouseleave', (e) => {
      // 在子视图状态下，完全不关闭菜单
      if (this.moreMenuView) {
        return;
      }
      // 检查是否是 768px-1279px 断点
      const width = window.innerWidth;
      if (width >= 768 && width <= 1279) {
        return;
      }
      this.closeDesktopSubmenu(this.moreMenuItem);
    });

    // Click 切换
    link.addEventListener('click', (e) => {
      e.preventDefault();
      this.toggleDesktopSubmenu(this.moreMenuItem);
    });

    // OUR TEAM 链接点击 - 进入/退出 TEAM 视图
    if (this.moreTeamLink) {
      this.moreTeamLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.moreMenuView === 'team') {
          // 在 TEAM 视图中点击标题，返回默认视图
          this.exitMoreView(false);
        } else {
          // 进入 TEAM 视图，保持下拉菜单展开
          this.enterMoreView('team');
          // 确保菜单保持展开状态
          this.moreMenuItem.setAttribute('aria-expanded', 'true');
        }
      });
    }

    // 首頁 链接点击 - 进入/退出 ZH 视图
    if (this.moreZhLink) {
      this.moreZhLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.moreMenuView === 'zh') {
          // 在 ZH 视图中点击标题，返回默认视图
          this.exitMoreView(false);
        } else {
          // 进入 ZH 视图，保持下拉菜单展开
          this.enterMoreView('zh');
          // 确保菜单保持展开状态
          this.moreMenuItem.setAttribute('aria-expanded', 'true');
        }
      });
    }

    // 更多菜单内的团队切换（768px-1279px 时使用）
    if (this.moreTeamToggle) {
      this.moreTeamToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // 直接进入团队视图
        if (this.moreMenuView !== 'team') {
          this.enterMoreView('team');
        }
      });
    }

    // 更多菜单内的中文切换（768px-1279px 时使用）
    if (this.moreZhToggle) {
      this.moreZhToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // 直接进入中文视图
        if (this.moreMenuView !== 'zh') {
          this.enterMoreView('zh');
        }
      });
    }

    // 更多菜单内的"我們的團隊"切换
    if (this.moreZhTeamToggle) {
      this.moreZhTeamToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMoreZhTeam();
      });
    }

    // 點擊"我們的團隊"標題鏈接（展開後顯示的灰色鏈接）時收起子菜單
    const moreZhTitleLink = this.moreDropdown?.querySelector('.more-zh-title-link');
    if (moreZhTitleLink) {
      moreZhTitleLink.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // 收起團隊子菜單，返回 ZH 視圖的默認狀態
        this.closeMoreZhTeam();
      });
    }

    // 点击更多菜单内部时不关闭
    if (this.moreDropdown) {
      this.moreDropdown.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // 更多菜单容器点击阻止冒泡
    this.moreMenuItem.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    // 点击外部关闭更多菜单（统一处理）
    document.addEventListener('click', (e) => {
      // 检查点击是否在更多菜单外部
      if (!this.moreMenuItem.contains(e.target)) {
        // 如果有子视图，关闭整个菜单
        if (this.moreMenuView) {
          this.exitMoreView(true);
        }
        // 如果是默认视图且菜单打开，关闭菜单
        else if (this.moreMenuItem.getAttribute('aria-expanded') === 'true') {
          this.closeDesktopSubmenu(this.moreMenuItem);
        }
      }
      // 如果在子视图状态下点击子菜单内部（OUR AMBASSADOR 等链接），保持菜单打开
      else if (this.moreMenuView && this.moreDropdown.contains(e.target)) {
        // 点击的是子菜单链接，保持菜单打开
        e.stopPropagation();
      }
    });
  }

  /**
   * 切换 TEAM 视图
   */
  toggleMoreTeamView() {
    if (this.moreMenuView === 'team') {
      this.exitMoreView();
    } else {
      this.enterMoreView('team');
    }
  }

  /**
   * 切换 ZH 视图
   */
  toggleMoreZhView() {
    if (this.moreMenuView === 'zh') {
      this.exitMoreView();
    } else {
      this.enterMoreView('zh');
    }
  }

  /**
   * 进入子视图
   */
  enterMoreView(view) {
    if (!this.moreDropdown) return;

    this.moreMenuView = view;
    this.moreDropdown.classList.add(`is-${view}-view`);
    this.moreMenuItem.classList.add('is-view-active');

    // 确保菜单保持展开状态
    this.moreMenuItem.setAttribute('aria-expanded', 'true');
    const submenu = this.moreMenuItem.querySelector('.submenu');
    if (submenu) {
      submenu.style.display = 'block';
      submenu.setAttribute('aria-hidden', 'false');
    }

    // 根据视图类型展开对应的子菜单
    if (view === 'team') {
      this.openMoreTeam();
    } else if (view === 'zh') {
      this.openMoreZh();
    }
  }

  /**
   * 退出子视图
   */
  exitMoreView(closeMenu = false) {
    if (!this.moreDropdown) return;

    this.moreDropdown.classList.remove('is-team-view', 'is-zh-view');
    this.moreMenuItem.classList.remove('is-view-active');
    this.moreMenuView = null;

    // 重置所有展开状态
    this.moreTeamToggle?.setAttribute('aria-expanded', 'false');
    this.moreZhToggle?.setAttribute('aria-expanded', 'false');
    this.moreZhTeamToggle?.setAttribute('aria-expanded', 'false');
    this.isMoreTeamExpanded = false;
    this.isMoreZhExpanded = false;
    this.isMoreZhTeamExpanded = false;

    // 隐藏所有子菜单
    const moreTeamSubmenu = document.querySelector('.more-team-submenu');
    const moreZhSubmenu = document.querySelector('.more-zh-submenu');
    const moreZhTeamSubmenu = document.querySelector('.more-zh-team-submenu');

    if (moreTeamSubmenu) {
      moreTeamSubmenu.setAttribute('aria-hidden', 'true');
    }
    if (moreZhSubmenu) {
      moreZhSubmenu.setAttribute('aria-hidden', 'true');
      moreZhSubmenu.classList.remove('is-team-expanded');
    }
    if (moreZhTeamSubmenu) {
      moreZhTeamSubmenu.setAttribute('aria-hidden', 'true');
    }

    // 如果指定关闭菜单，关闭整个更多菜单
    if (closeMenu) {
      this.closeDesktopSubmenu(this.moreMenuItem);
    }
  }

  /**
   * 桌面端下拉菜单
   */
  initDesktopMenu() {
    this.desktopMenuItems.forEach(item => {
      const link = item.querySelector('.nav-link');
      const submenu = item.querySelector('.submenu');

      if (!link || !submenu) return;

      // 检查是否是中文导航项
      const isZhNav = item.matches('.nav-menu > li:nth-last-of-type(2)');

      // Hover 打开
      item.addEventListener('mouseenter', () => {
        this.openDesktopSubmenu(item);
      });

      // Hover 关闭
      item.addEventListener('mouseleave', () => {
        // 如果是中文导航且团队子菜单展开，不要关闭
        if (isZhNav && this.isZhTeamExpanded) {
          return;
        }
        this.closeDesktopSubmenu(item);
      });

      // Click 切换 (用于触摸设备)
      link.addEventListener('click', (e) => {
        // 如果有子菜单且当前不是展开状态，阻止默认行为
        if (!item.getAttribute('aria-expanded') || item.getAttribute('aria-expanded') === 'false') {
          e.preventDefault();
          this.toggleDesktopSubmenu(item);
        }
      });
    });
  }

  /**
   * 切换更多菜单内的团队子菜单
   */
  toggleMoreTeam() {
    if (this.isMoreTeamExpanded) {
      this.closeMoreTeam();
    } else {
      this.openMoreTeam();
    }
  }

  openMoreTeam() {
    const teamSubmenu = document.querySelector('.more-team-submenu');
    const teamWrapper = document.querySelector('.more-team-wrapper');
    if (!teamSubmenu || !teamWrapper) return;

    teamSubmenu.setAttribute('aria-hidden', 'false');
    if (this.moreTeamToggle) {
      this.moreTeamToggle.setAttribute('aria-expanded', 'true');
    }
    this.isMoreTeamExpanded = true;
  }

  closeMoreTeam() {
    const teamSubmenu = document.querySelector('.more-team-submenu');
    if (teamSubmenu) {
      teamSubmenu.setAttribute('aria-hidden', 'true');
      if (this.moreTeamToggle) {
        this.moreTeamToggle.setAttribute('aria-expanded', 'false');
      }
      this.isMoreTeamExpanded = false;
    }
  }

  /**
   * 切换更多菜单内的中文子菜单
   */
  toggleMoreZh() {
    if (this.isMoreZhExpanded) {
      this.closeMoreZh();
    } else {
      this.openMoreZh();
    }
  }

  openMoreZh() {
    const zhSubmenu = this.moreDropdown.querySelector('.more-zh-submenu');
    const zhWrapper = this.moreDropdown.querySelector('.more-zh-wrapper');
    if (!zhSubmenu || !zhWrapper) return;

    zhSubmenu.style.display = 'block';
    zhSubmenu.setAttribute('aria-hidden', 'false');
    if (this.moreZhToggle) {
      this.moreZhToggle.setAttribute('aria-expanded', 'true');
    }
    this.isMoreZhExpanded = true;

    // 重置团队展开状态
    zhSubmenu.classList.remove('is-team-expanded');
  }

  closeMoreZh() {
    const zhSubmenu = this.moreDropdown.querySelector('.more-zh-submenu');
    if (zhSubmenu) {
      zhSubmenu.setAttribute('aria-hidden', 'true');
      if (this.moreZhToggle) {
        this.moreZhToggle.setAttribute('aria-expanded', 'false');
      }
      this.isMoreZhExpanded = false;
    }
  }

  /**
   * 切换更多菜单内"我們的團隊"子菜单
   */
  toggleMoreZhTeam() {
    if (this.isMoreZhTeamExpanded) {
      this.closeMoreZhTeam();
    } else {
      this.openMoreZhTeam();
    }
  }

  openMoreZhTeam() {
    const teamSubmenu = this.moreDropdown.querySelector('.more-zh-team-submenu');
    const zhSubmenu = this.moreDropdown.querySelector('.more-zh-submenu');
    if (!teamSubmenu) return;
    teamSubmenu.setAttribute('aria-hidden', 'false');
    if (this.moreZhTeamToggle) {
      this.moreZhTeamToggle.setAttribute('aria-expanded', 'true');
    }
    this.isMoreZhTeamExpanded = true;

    // 添加展开状态类到父级子菜单（用于隐藏常规项）
    if (zhSubmenu) {
      zhSubmenu.classList.add('is-team-expanded');
    }
  }

  closeMoreZhTeam() {
    const teamSubmenu = this.moreDropdown.querySelector('.more-zh-team-submenu');
    const zhSubmenu = this.moreDropdown.querySelector('.more-zh-submenu');
    if (teamSubmenu) {
      teamSubmenu.setAttribute('aria-hidden', 'true');
      if (this.moreZhTeamToggle) {
        this.moreZhTeamToggle.setAttribute('aria-expanded', 'false');
      }
      this.isMoreZhTeamExpanded = false;
    }
    // 移除展开状态类
    if (zhSubmenu) {
      zhSubmenu.classList.remove('is-team-expanded');
    }
  }

  /**
   * 桌面端下拉菜单
   */
  initDesktopMenu() {
    this.desktopMenuItems.forEach(item => {
      const link = item.querySelector('.nav-link');
      const submenu = item.querySelector('.submenu');

      if (!link || !submenu) return;

      // 检查是否是中文导航项
      const isZhNav = item.matches('.nav-menu > li:nth-last-of-type(2)');

      // Hover 打开
      item.addEventListener('mouseenter', () => {
        this.openDesktopSubmenu(item);
      });

      // Hover 关闭
      item.addEventListener('mouseleave', () => {
        // 如果是中文导航且团队子菜单展开，不要关闭
        if (isZhNav && this.isZhTeamExpanded) {
          return;
        }
        this.closeDesktopSubmenu(item);
      });

      // Click 切换 (用于触摸设备)
      link.addEventListener('click', (e) => {
        // 如果有子菜单且当前不是展开状态，阻止默认行为
        if (!item.getAttribute('aria-expanded') || item.getAttribute('aria-expanded') === 'false') {
          e.preventDefault();
          this.toggleDesktopSubmenu(item);
        }
      });
    });
  }

  openDesktopSubmenu(item) {
    // 关闭其他所有已打开的二级菜单
    this.desktopMenuItems.forEach(otherItem => {
      if (otherItem !== item) {
        this.closeDesktopSubmenu(otherItem);
      }
    });

    const submenu = item.querySelector('.submenu');
    if (submenu) {
      item.setAttribute('aria-expanded', 'true');
      submenu.style.display = 'block';
      submenu.setAttribute('aria-hidden', 'false');
    }
  }

  closeDesktopSubmenu(item) {
    // 如果是更多菜单且在子视图状态下，不关闭
    if (item.classList.contains('more-menu-item') && this.moreMenuView) {
      return;
    }

    const submenu = item.querySelector('.submenu');
    if (submenu) {
      item.setAttribute('aria-expanded', 'false');
      submenu.style.display = 'none';
      submenu.setAttribute('aria-hidden', 'true');

      // 如果是中文导航，关闭团队子菜单
      if (item.matches('.nav-menu > li:nth-last-of-type(2)')) {
        const navDropdown = item.querySelector('.dropdown-menu');
        const teamToggle = item.querySelector('.zh-team-toggle');
        if (navDropdown) {
          navDropdown.classList.remove('is-team-expanded');
        }
        if (teamToggle) {
          teamToggle.setAttribute('aria-expanded', 'false');
        }
        this.isZhTeamExpanded = false;
      }

      // 如果是更多菜单，关闭团队和中文子菜单（除非在子视图状态下）
      if (item.classList.contains('more-menu-item')) {
        // 在子视图状态下不关闭子菜单，由 exitMoreView 处理
        if (!this.moreMenuView) {
          this.closeMoreTeam();
          this.closeMoreZh();
          this.closeMoreZhTeam();
          this.exitMoreView(false);
        }
      }
    }
  }

  toggleDesktopSubmenu(item) {
    const isExpanded = item.getAttribute('aria-expanded') === 'true';

    // 关闭所有其他菜单
    this.desktopMenuItems.forEach(otherItem => {
      if (otherItem !== item) {
        this.closeDesktopSubmenu(otherItem);
      }
    });

    // 切换当前菜单
    if (isExpanded) {
      this.closeDesktopSubmenu(item);
    } else {
      this.openDesktopSubmenu(item);
    }
  }

  /**
   * 移动端侧边菜单
   */
  initMobileMenu() {
    // 防止重複綁定
    if (this.mobileMenuEventBound) return;
    this.mobileMenuEventBound = true;

    // 汉堡菜单按钮
    if (this.mobileMenuBtn) {
      this.mobileMenuBtn.addEventListener('click', () => this.toggleMobileMenu());
    }

    // 关闭按钮
    this.mobileMenuClose = document.querySelector('.mobile-menu-close');
    if (this.mobileMenuClose) {
      this.mobileMenuClose.addEventListener('click', () => this.closeMobileMenu());
    }

    // 点击遮罩关闭
    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.addEventListener('click', () => this.closeMobileMenu());
    }

    // 移动端子菜单切换 - 直接绑定到每个 toggle 按钮
    const toggles = this.mobileMenu.querySelectorAll('.mobile-nav-toggle');
    toggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        const navItem = toggle.closest('.mobile-nav-item');
        this.toggleMobileSubmenu(navItem);
      });
    });
  }

  toggleMobileMenu() {
    if (this.isMobileMenuOpen) {
      this.closeMobileMenu();
    } else {
      this.openMobileMenu();
    }
  }

  openMobileMenu() {
    if (this.mobileMenu && this.mobileMenuOverlay) {
      this.mobileMenu.classList.add('open');
      this.mobileMenuOverlay.classList.add('show');
      this.mobileMenuBtn.setAttribute('aria-expanded', 'true');
      this.mobileMenuBtn.setAttribute('aria-label', 'Close menu');
      this.isMobileMenuOpen = true;

      // 阻止 body 滚动
      document.body.style.overflow = 'hidden';
    }
  }

  closeMobileMenu() {
    if (this.mobileMenu && this.mobileMenuOverlay) {
      this.mobileMenu.classList.remove('open');
      this.mobileMenuOverlay.classList.remove('show');
      this.mobileMenuBtn.setAttribute('aria-expanded', 'false');
      this.mobileMenuBtn.setAttribute('aria-label', 'Show sidebar');
      this.isMobileMenuOpen = false;

      // 恢复 body 滚动
      document.body.style.overflow = '';

      // 清理所有toggle的高亮状态
      const allToggles = this.mobileMenu.querySelectorAll('.mobile-nav-toggle');
      allToggles.forEach(t => {
        t.classList.remove('active-toggle');
      });
    }
  }

  toggleMobileSubmenu(navItem) {
    if (!navItem) return;

    const submenu = navItem.querySelector(':scope > .mobile-submenu');
    const toggle = navItem.querySelector('.mobile-nav-toggle');

    if (!submenu || !toggle) return;

    // 切换当前菜单
    const wasExpanded = navItem.classList.contains('expanded');
    navItem.classList.toggle('expanded');

    const nowExpanded = navItem.classList.contains('expanded');

    // 管理高亮状态：移除所有其他toggle的高亮，给当前toggle添加高亮（只有当展开时）
    const allToggles = this.mobileMenu.querySelectorAll('.mobile-nav-toggle');
    allToggles.forEach(t => {
      t.classList.remove('active-toggle');
    });

    // 如果当前是展开状态，给当前toggle添加高亮
    if (nowExpanded) {
      toggle.classList.add('active-toggle');
    }

    if (nowExpanded) {
      submenu.classList.add('show');
    } else {
      submenu.classList.remove('show');
    }
    toggle.setAttribute('aria-expanded', nowExpanded);
  }

  /**
   * 搜索面板
   */
  initSearchPanel() {
    if (!this.searchToggle) return;

    this.searchToggle.addEventListener('click', () => this.toggleSearchPanel());

    // 搜索面板的打开/关闭/遮罩点击由 Vue 组件内部处理
    // menu.js 只保留 search-toggle 按钮的点击事件
  }

  toggleSearchPanel() {
    if (this.isSearchOpen) {
      this.closeSearchPanel();
    } else {
      this.openSearchPanel();
    }
  }

  openSearchPanel() {
    if (window.searchPanelApp && typeof window.searchPanelApp.openPanel === 'function') {
      window.searchPanelApp.openPanel();
    }
    this.searchToggle.setAttribute('aria-expanded', 'true');
    this.isSearchOpen = true;
  }

  closeSearchPanel() {
    if (window.searchPanelApp && typeof window.searchPanelApp.closePanel === 'function') {
      window.searchPanelApp.closePanel();
    }
    this.searchToggle.setAttribute('aria-expanded', 'false');
    this.isSearchOpen = false;
  }

  /**
   * 键盘导航
   */
  initKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      // Escape 键关闭所有菜单
      if (e.key === 'Escape') {
        this.closeMobileMenu();
        this.closeSearchPanel();

        // 关闭所有桌面端下拉菜单
        this.desktopMenuItems.forEach(item => {
          this.closeDesktopSubmenu(item);
        });

        // 关闭中文导航团队子菜单
        if (this.isZhTeamExpanded) {
          this.closeZhTeam();
        }
      }

      // Tab 键导航 (可访问性)
      if (e.key === 'Tab') {
        this.handleTabNavigation(e);
      }
    });
  }

  handleTabNavigation(e) {
    // 实现键盘导航逻辑
    // 在实际项目中可以添加更完善的键盘导航支持
  }

  /**
   * Search Panel 滚动监听 - 添加阴影效果
   * 独立于 site-header 的滚动监听
   */
  initSearchPanelScrollListener() {
    // Vue 组件渲染后获取 search-panel 元素
    const searchPanel = document.querySelector('.search-panel');
    if (!searchPanel) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (window.scrollY > 10) {
            searchPanel.classList.add('scrolled');
          } else {
            searchPanel.classList.remove('scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });
  }


  /**
   * 滚动监听 - 添加阴影效果
   */
  initScrollListener() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    let ticking = false;

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          if (window.scrollY > 10) {
            header.classList.add('scrolled');
          } else {
            header.classList.remove('scrolled');
          }
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  /**
   * 中文导航特殊交互
   * 处理"首頁"下拉菜单和"我們的團隊"切换功能
   */
  initZhNav() {
    if (!this.zhNavItem || !this.zhNavDropdown) return;

    // 切换按钮点击事件
    if (this.zhTeamToggle) {
      this.zhTeamToggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // 确保下拉菜单是打开的
        if (this.zhNavItem && this.zhNavItem.getAttribute('aria-expanded') !== 'true') {
          this.openDesktopSubmenu(this.zhNavItem);
        }

        this.toggleZhTeam();
      });
    }

    // 鼠标离开中文导航项时，如果团队子菜单未展开则关闭下拉菜单
    this.zhNavItem.addEventListener('mouseleave', () => {
      // 只有当团队子菜单未展开时才关闭
      if (!this.isZhTeamExpanded) {
        this.closeDesktopSubmenu(this.zhNavItem);
      }
      // 如果团队子菜单展开，保持下拉菜单打开
    });

    // 鼠标进入下拉菜单时保持状态
    this.zhNavDropdown.addEventListener('mouseenter', () => {
      this.isZhNavHovered = true;
    });

    // 鼠标离开下拉菜单时，如果团队子菜单展开则不关闭，否则关闭
    this.zhNavDropdown.addEventListener('mouseleave', () => {
      this.isZhNavHovered = false;
      // 只有在团队子菜单未展开时才关闭下拉菜单
      if (!this.isZhTeamExpanded) {
        this.closeDesktopSubmenu(this.zhNavItem);
      }
      // 如果团队子菜单展开，保持下拉菜单打开
    });

    // 点击页面其他区域关闭菜单
    document.addEventListener('click', (e) => {
      if (this.zhNavItem) {
        // 检查点击是否在中文导航区域外
        if (!this.zhNavItem.contains(e.target)) {
          // 关闭团队子菜单（如果展开）
          if (this.isZhTeamExpanded) {
            this.closeZhTeam();
          }
          // 关闭下拉菜单
          this.closeDesktopSubmenu(this.zhNavItem);
        }
      }
    });

    // 點擊計畫大使链接后关闭菜单并跳转
    const ambassadorLink = this.zhNavDropdown.querySelector('.zh-ambassador-link');
    if (ambassadorLink) {
      ambassadorLink.addEventListener('click', (e) => {
        // 允许默认导航行为
        // 页面跳转后会自然关闭菜单
      });
    }

    // 1280px+ 断点："我們的團隊"链接点击处理
    // const zhTeamLink = this.zhNavDropdown.querySelector('.zh-team-link');
    const zhTeamLink = this.zhNavDropdown.querySelector('.zh-team-wrapper .submenu-link');
    if (zhTeamLink) {
      zhTeamLink.addEventListener('click', (e) => {
        // 检查是否是 1280px+ 断点
        const width = window.innerWidth;
        if (width >= 1280) {
          // 如果子菜单已展开，阻止跳转并收起子菜单
          if (this.isZhTeamExpanded) {
            e.preventDefault();
            e.stopPropagation();
            this.closeZhTeam();
          }
        }
      });
    }
  }

  /**
   * 切换"我們的團隊"子菜单显示状态
   */
  toggleZhTeam() {
    if (this.isZhTeamExpanded) {
      this.closeZhTeam();
    } else {
      this.openZhTeam();
    }
  }

  /**
   * 展开"我們的團隊"子菜单
   * 隐藏常规菜单项，显示計畫大使
   */
  openZhTeam() {
    if (!this.zhNavDropdown || !this.zhTeamToggle) return;

    // 确保下拉菜单是打开的
    if (this.zhNavItem && !this.isZhNavHovered) {
      this.openDesktopSubmenu(this.zhNavItem);
    }

    this.zhNavDropdown.classList.add('is-team-expanded');
    this.zhTeamToggle.setAttribute('aria-expanded', 'true');
    this.isZhTeamExpanded = true;
  }

  /**
   * 关闭"我們的團隊"子菜单
   * 显示常规菜单项，隐藏計畫大使（保持下拉菜单打开）
   */
  closeZhTeam() {
    if (!this.zhNavDropdown || !this.zhTeamToggle) return;

    this.zhNavDropdown.classList.remove('is-team-expanded');
    this.zhTeamToggle.setAttribute('aria-expanded', 'false');
    this.isZhTeamExpanded = false;
    // 注意：不关闭下拉菜单，只有在鼠标离开下拉菜单区域时才关闭
  }
}

/**
 * 初始化导航菜单
 * 在 DOM 加载完成后执行
 */
if (document.readyState === 'complete' || document.readyState === 'interactive') {
  setTimeout(() => {
    window.navigationMenu = new NavigationMenu();
  }, 1);
} else {
  document.addEventListener('DOMContentLoaded', () => {
    window.navigationMenu = new NavigationMenu();
  });
}

// 导出供外部使用 (ES modules)
if (typeof module !== 'undefined' && module.exports) {
  module.exports = NavigationMenu;
}
